import { useEffect, useState, useCallback } from "react";
import { tr } from "@/utils/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/utils/api";
import { Users, Trash2, Eye, Edit3 } from "lucide-react";

/**
 * Props:
 *  - cycleId: required
 *  - getAuthHeader: () => headers
 *  - language
 *  - refreshKey: bump to force refetch (after adding)
 */
export function CollaboratorsList({ cycleId, getAuthHeader, language, refreshKey = 0 }) {
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [removingEmail, setRemovingEmail] = useState(null);

  const fetchList = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    try {
      const res = await api.get(`/cycles/${cycleId}/collaborators`, { headers: getAuthHeader() });
      setCollaborators(res.data.collaborators || []);
    } catch (e) {
      // Silent on 404 (cycle not persisted); surface other errors
      if (e.response?.status && e.response.status !== 404) {
        console.warn("Failed to load collaborators:", e.response?.data?.detail || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [cycleId, getAuthHeader]);

  useEffect(() => { fetchList(); }, [fetchList, refreshKey]);

  const handleRemove = async (email) => {
    setRemovingEmail(email);
    try {
      await api.delete(`/cycles/${cycleId}/collaborators/${encodeURIComponent(email)}`, {
        headers: getAuthHeader(),
      });
      setCollaborators(prev => prev.filter(c => c.email !== email));
      toast.success(
        tr(language, "Collaborator removed", "Colaborador removido", "Colaborador eliminado")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove collaborator");
    } finally {
      setRemovingEmail(null);
    }
  };

  const sectionLabel =
    tr(language, "Invited collaborators", "Colaboradores convidados", "Colaboradores invitados");

  const emptyLabel =
    tr(language, "No collaborators yet. Invite via email above.", "Nenhum colaborador ainda. Convide por email acima.", "Aún no hay colaboradores. Invita por email arriba.");

  return (
    <div className="space-y-2" data-testid="collaborators-list">
      <div className="flex items-center gap-2 text-zinc-400 text-sm">
        <Users className="w-4 h-4 text-cyan-400" />
        {sectionLabel}
        {collaborators.length > 0 && (
          <Badge className="bg-cyan-500/20 text-cyan-400">{collaborators.length}</Badge>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500">Loading…</div>
      ) : collaborators.length === 0 ? (
        <div className="p-3 bg-zinc-900/50 rounded-lg text-xs text-zinc-500" data-testid="collaborators-empty">
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-2">
          {collaborators.map((c) => (
            <div key={c.email} className="flex items-center gap-2 p-2 bg-zinc-900/50 rounded-lg" data-testid={`collaborator-${c.email}`}>
              <span className="flex-1 text-sm text-white truncate">{c.email}</span>
              <Badge className={c.permission === 'edit' ? 'bg-violet-500/20 text-violet-400' : 'bg-zinc-700 text-zinc-300'}>
                {c.permission === 'edit' ? (
                  <><Edit3 className="w-3 h-3 mr-1 inline" />{tr(language, 'Edit', 'Edita', 'Edita')}</>
                ) : (
                  <><Eye className="w-3 h-3 mr-1 inline" />{tr(language, 'View', 'Lê', 'Lee')}</>
                )}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-red-400 hover:text-red-300"
                disabled={removingEmail === c.email}
                onClick={() => handleRemove(c.email)}
                data-testid={`remove-collaborator-${c.email}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
